<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Themes</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage themes for offerings</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="showImportModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                </svg>
                                                Import Theme
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Theme
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[200px]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search themes..."
                                                                class="w-full max-w-[300px] py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">{{ filteredItems.length }} themes</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <label class="text-sm text-gray-500">Offering:</label>
                                                <div class="relative min-w-[260px]" ref="filterOfferingDropdownRef">
                                                        <div class="relative">
                                                                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                                </svg>
                                                                <input
                                                                        v-model="filterOfferingSearch"
                                                                        type="text"
                                                                        :placeholder="selectedFilterOffering ? '' : 'All Offerings — search by ID, name, external ID...'"
                                                                        class="w-full py-2 pl-9 pr-8 border border-gray-300 rounded-md text-sm bg-white box-border outline-none"
                                                                        @focus="filterOfferingDropdownOpen = true"
                                                                        @input="onFilterOfferingInput"
                                                                >
                                                                <button
                                                                        v-if="selectedFilterOffering || filterOfferingSearch"
                                                                        type="button"
                                                                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-1"
                                                                        title="Clear"
                                                                        @click="clearFilterOffering"
                                                                >
                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                        </svg>
                                                                </button>
                                                        </div>
                                                        <div
                                                                v-if="filterOfferingDropdownOpen && filteredFilterOfferings.length > 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-[260px] overflow-y-auto"
                                                        >
                                                                <div
                                                                        class="py-2 px-3 text-sm cursor-pointer hover:bg-orange-1 transition-colors text-gray-600 border-b border-gray-100"
                                                                        @mousedown.prevent="clearFilterOffering"
                                                                >
                                                                        All Offerings
                                                                </div>
                                                                <div
                                                                        v-for="off in filteredFilterOfferings"
                                                                        :key="off.id"
                                                                        class="py-2 px-3 text-sm cursor-pointer hover:bg-orange-1 transition-colors"
                                                                        :class="{ 'bg-orange-1': selectedFilterOffering && selectedFilterOffering.id === off.id }"
                                                                        @mousedown.prevent="selectFilterOffering(off)"
                                                                >
                                                                        <span class="font-mono text-gray-500">{{ off.id }}</span>
                                                                        <span class="mx-1 text-gray-300">—</span>
                                                                        <span class="font-medium text-gray-900">{{ off.name }}</span>
                                                                        <span v-if="off.externalid" class="ml-2 text-xs text-gray-400">({{ off.externalid }})</span>
                                                                </div>
                                                        </div>
                                                        <div
                                                                v-else-if="filterOfferingDropdownOpen && filterOfferingSearch.trim() && filteredFilterOfferings.length === 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg"
                                                        >
                                                                <div class="py-3 px-3 text-sm text-gray-400 text-center">No offerings found</div>
                                                        </div>
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <label class="text-sm text-gray-500">Show:</label>
                                                <select v-model="pageSize" class="py-2 px-3 border border-gray-300 rounded-md text-sm bg-white">
                                                        <option :value="10">10</option>
                                                        <option :value="25">25</option>
                                                        <option :value="50">50</option>
                                                        <option :value="100">100</option>
                                                </select>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading themes...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="text-left py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase w-[80px]">Icon</th>
                                                                <th class="text-left py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">ID</th>
                                                                <th class="text-left py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">Name</th>
                                                                <th class="text-right py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase w-[120px]">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="paginatedItems.length === 0">
                                                                <td colspan="4" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No themes found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Try adjusting your search or filters</p>
                                                                </td>
                                                        </tr>
                                                        <tr
                                                                v-for="item in paginatedItems"
                                                                :key="item.id"
                                                                class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                @click="editItem(item.id)"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-900 align-middle">
                                                                        <img
                                                                                v-if="item.icon"
                                                                                :src="item.icon"
                                                                                :alt="item.name"
                                                                                class="w-10 h-10 rounded object-cover"
                                                                        >
                                                                        <div v-else class="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">-</div>
                                                                </td>
                                                                <td class="py-3 px-4 text-sm font-mono text-gray-500 align-middle">{{ item.id }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-900 font-medium align-middle">{{ item.name }}</td>
                                                                <td class="py-3 px-4 text-sm text-right align-middle">
                                                                        <div class="flex justify-end gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                        title="Edit"
                                                                                        @click.stop="editItem(item.id)"
                                                                                >
                                                                                        <IconEdit class="w-5 h-5" />
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                        title="Delete"
                                                                                        @click.stop="confirmDelete(item)"
                                                                                >
                                                                                        <IconTrash class="w-5 h-5" />
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 border-t border-gray-200">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} entries
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-[2.5rem]"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="closeCreateModal"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-[400px] m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create New Theme</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="closeCreateModal"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                                <input
                                                        v-model="newThemeName"
                                                        type="text"
                                                        placeholder="Enter theme name"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border outline-none"
                                                >
                                        </div>
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Offering *</label>
                                                <div class="relative" ref="createOfferingDropdownRef">
                                                        <input
                                                                v-model="createOfferingSearch"
                                                                type="text"
                                                                placeholder="Search by ID, name, external ID, or description..."
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border outline-none"
                                                                @focus="createOfferingDropdownOpen = true"
                                                                @input="onCreateOfferingInput"
                                                        >
                                                        <div
                                                                v-if="createOfferingDropdownOpen && filteredCreateOfferings.length > 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-[200px] overflow-y-auto"
                                                        >
                                                                <div
                                                                        v-for="off in filteredCreateOfferings"
                                                                        :key="off.id"
                                                                        class="py-2 px-3 text-sm cursor-pointer hover:bg-orange-1 transition-colors"
                                                                        @mousedown.prevent="selectCreateOffering(off)"
                                                                >
                                                                        <span class="font-mono text-gray-500">{{ off.id }}</span>
                                                                        <span class="mx-1 text-gray-300">—</span>
                                                                        <span class="font-medium text-gray-900">{{ off.name }}</span>
                                                                        <span v-if="off.externalid" class="ml-2 text-xs text-gray-400">({{ off.externalid }})</span>
                                                                </div>
                                                        </div>
                                                        <div
                                                                v-else-if="createOfferingDropdownOpen && createOfferingSearch.trim() && filteredCreateOfferings.length === 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg"
                                                        >
                                                                <div class="py-3 px-3 text-sm text-gray-400 text-center">No offerings found</div>
                                                        </div>
                                                </div>
                                                <div v-if="selectedCreateOffering" class="mt-2 flex items-center gap-2 bg-orange-1 border border-orange-3 rounded-md py-1.5 px-3 text-sm">
                                                        <span class="text-orange-8 font-medium">{{ selectedCreateOffering.name }}</span>
                                                        <span class="text-orange-5 text-xs">(ID: {{ selectedCreateOffering.id }})</span>
                                                        <button
                                                                type="button"
                                                                class="ml-auto text-orange-5 hover:text-orange-7 bg-transparent border-none cursor-pointer p-0"
                                                                @click="clearCreateOffering"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200">
                                        <button
                                                type="button"
                                                class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-md py-2 px-4 text-sm font-medium cursor-pointer"
                                                @click="closeCreateModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                class="bg-orange-7 hover:bg-orange-6 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                :disabled="!newThemeName.trim() || !selectedCreateOffering || isCreating"
                                                @click="createItem"
                                        >
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>
                <!-- Import Modal -->
                <div v-if="showImportModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="closeImportModal"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-[520px] m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Import Theme</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="closeImportModal"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Offering *</label>
                                                <div class="relative" ref="offeringDropdownRef">
                                                        <input
                                                                v-model="importOfferingSearch"
                                                                type="text"
                                                                placeholder="Search by ID, name, external ID, or description..."
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border outline-none"
                                                                @focus="importOfferingDropdownOpen = true"
                                                                @input="importOfferingDropdownOpen = true"
                                                        >
                                                        <div
                                                                v-if="importOfferingDropdownOpen && filteredImportOfferings.length > 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-[200px] overflow-y-auto"
                                                        >
                                                                <div
                                                                        v-for="off in filteredImportOfferings"
                                                                        :key="off.id"
                                                                        class="py-2 px-3 text-sm cursor-pointer hover:bg-orange-1 transition-colors"
                                                                        @mousedown.prevent="selectImportOffering(off)"
                                                                >
                                                                        <span class="font-mono text-gray-500">{{ off.id }}</span>
                                                                        <span class="mx-1 text-gray-300">—</span>
                                                                        <span class="font-medium text-gray-900">{{ off.name }}</span>
                                                                        <span v-if="off.externalid" class="ml-2 text-xs text-gray-400">({{ off.externalid }})</span>
                                                                </div>
                                                        </div>
                                                        <div
                                                                v-else-if="importOfferingDropdownOpen && importOfferingSearch.trim() && filteredImportOfferings.length === 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg"
                                                        >
                                                                <div class="py-3 px-3 text-sm text-gray-400 text-center">No offerings found</div>
                                                        </div>
                                                </div>
                                                <div v-if="selectedImportOffering" class="mt-2 flex items-center gap-2 bg-orange-1 border border-orange-3 rounded-md py-1.5 px-3 text-sm">
                                                        <span class="text-orange-8 font-medium">{{ selectedImportOffering.name }}</span>
                                                        <span class="text-orange-5 text-xs">(ID: {{ selectedImportOffering.id }})</span>
                                                        <button
                                                                type="button"
                                                                class="ml-auto text-orange-5 hover:text-orange-7 bg-transparent border-none cursor-pointer p-0"
                                                                @click="clearImportOffering"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </div>

                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Theme File *</label>

                                                <input
                                                        ref="importFileInputRef"
                                                        type="file"
                                                        accept=".json,.zip,application/json,application/zip"
                                                        class="hidden"
                                                        @change="handleImportFileSelect"
                                                >

                                                <div
                                                        v-if="!importFile"
                                                        class="border-2 border-dashed rounded-xl py-8 px-4 text-center cursor-pointer transition-colors hover:border-orange-7 hover:bg-orange-1"
                                                        :class="importIsDragging ? 'border-orange-7 bg-orange-1' : 'border-gray-300'"
                                                        @click="triggerImportFileInput"
                                                        @dragenter.prevent="importIsDragging = true"
                                                        @dragover.prevent="importIsDragging = true"
                                                        @dragleave.prevent="importIsDragging = false"
                                                        @drop.prevent="handleImportFileDrop"
                                                >
                                                        <svg class="w-10 h-10 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                        </svg>
                                                        <p class="text-sm text-gray-600 mb-1">
                                                                <span class="text-orange-7 font-medium">Click to upload</span> or drag and drop
                                                        </p>
                                                        <p class="text-xs text-gray-400">Drop your theme file here</p>
                                                </div>

                                                <div v-else class="relative border border-gray-200 rounded-xl p-4 bg-gray-50">
                                                        <div class="flex items-center gap-4">
                                                                <div class="w-10 h-10 bg-white rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 border border-gray-200">
                                                                        <svg class="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                        </svg>
                                                                </div>
                                                                <div class="flex-1 min-w-0">
                                                                        <p class="text-sm font-medium text-gray-900 m-0 truncate">{{ importFile.name }}</p>
                                                                        <p class="text-xs text-gray-400 m-0 mt-0.5">{{ formatFileSize(importFile.size) }}</p>
                                                                </div>
                                                                <button
                                                                        type="button"
                                                                        class="text-sm text-red-600 bg-transparent border-none cursor-pointer p-1"
                                                                        @click="importFile = null"
                                                                >
                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                        </svg>
                                                                </button>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200">
                                        <button
                                                type="button"
                                                class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-md py-2 px-4 text-sm font-medium cursor-pointer"
                                                @click="closeImportModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                :disabled="!selectedImportOffering || !importFile || isImporting"
                                                @click="importTheme"
                                        >
                                                <svg v-if="isImporting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isImporting ? 'Importing...' : 'Import' }}
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, type ComputedRef } from 'vue';
import { useRouter } from 'vue-router';
import { ThemeModel, OfferingModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import ThemeService from 'services/ThemeService';
import { IconEdit, IconTrash } from 'components/icons';
import { httpClient } from 'utils/http';

export default defineComponent({
        name: 'ThemesPage',
        components: { IconEdit, IconTrash },
        setup() {
                const router = useRouter();

                const items = ref<ThemeModel[]>([]);
                const offerings = ref<OfferingModel[]>([]);
                const selectedOfferingId = ref<string>('');
                const selectedFilterOffering = ref<OfferingModel | null>(null);
                const filterOfferingSearch = ref('');
                const filterOfferingDropdownOpen = ref(false);
                const filterOfferingDropdownRef = ref<HTMLElement | null>(null);
                const searchQuery = ref('');
                const currentPage = ref(1);
                const pageSize = ref(25);
                const isLoading = ref(true);
                const showCreateModal = ref(false);
                const newThemeName = ref('');
                const selectedCreateOffering = ref<OfferingModel | null>(null);
                const createOfferingSearch = ref('');
                const createOfferingDropdownOpen = ref(false);
                const createOfferingDropdownRef = ref<HTMLElement | null>(null);
                const isCreating = ref(false);

                const showImportModal = ref(false);
                const importOfferingSearch = ref('');
                const importOfferingDropdownOpen = ref(false);
                const selectedImportOffering = ref<OfferingModel | null>(null);
                const importFile = ref<File | null>(null);
                const importIsDragging = ref(false);
                const isImporting = ref(false);
                const importFileInputRef = ref<HTMLInputElement | null>(null);

                const filteredItems = computed(() => {
                        if (!searchQuery.value.trim()) {
                                return items.value;
                        }
                        const query = searchQuery.value.toLowerCase();
                        return items.value.filter(item =>
                                String(item.name ?? '').toLowerCase().includes(query) ||
                                String(item.id).includes(query)
                        );
                });

                const totalRows = computed(() => filteredItems.value.length);
                const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
                const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
                const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

                const paginatedItems = computed(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredItems.value.slice(start, start + pageSize.value);
                });

                const visiblePages: ComputedRef<number[]> = computed(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        let end = Math.min(totalPages.value, start + maxVisible - 1);
                        if (end - start + 1 < maxVisible) {
                                start = Math.max(1, end - maxVisible + 1);
                        }
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                watch([searchQuery, pageSize], () => {
                        currentPage.value = 1;
                });

                const goToPage = (page: number) => {
                        if (page >= 1 && page <= totalPages.value) {
                                currentPage.value = page;
                        }
                };

                const loadOfferings = async () => {
                        try {
                                const response = await httpClient.get<OfferingModel[]>('/api/offering?limit=0');
                                offerings.value = response.data;
                        } catch (error: any) {
                                console.error('Failed to load offerings:', error);
                        }
                };

                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                
                                if (selectedOfferingId.value) {
                                        const offering = offerings.value.find(o => String(o.id) === selectedOfferingId.value);
                                        if (offering) {
                                                params.append('where', JSON.stringify({
                                                        groupid: offering.groupid,
                                                        typeid: offering.typeid,
                                                }));
                                        }
                                }
                                
                                const data = await ThemeService.getAll(params);
                                items.value = data;
                        } catch (error: any) {
                                console.error('Failed to load themes:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load themes',
                                        icon: 'error',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const editItem = (id: number) => {
                        router.push(`/theme/${id}`);
                };

                const confirmDelete = async (item: ThemeModel) => {
                        const result = await Swal.fire({
                                title: 'Delete Theme?',
                                text: `Are you sure you want to delete "${item.name || 'this theme'}"? This action cannot be undone.`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                                cancelButtonText: 'Cancel',
                        });

                        if (result.isConfirmed) {
                                try {
                                        await ThemeService.delete(item.id);
                                        items.value = items.value.filter(i => i.id !== item.id);
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success',
                                                title: 'Theme deleted successfully',
                                                showConfirmButton: false,
                                                timer: 3000,
                                        });
                                } catch (error: any) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to delete theme',
                                                icon: 'error',
                                        });
                                }
                        }
                };

                const filteredCreateOfferings = computed(() => {
                        const query = createOfferingSearch.value.toLowerCase().trim();
                        const source = offerings.value;
                        if (!query) return source.slice(0, 50);
                        return source.filter(off =>
                                String(off.id).includes(query) ||
                                String(off.name ?? '').toLowerCase().includes(query) ||
                                (off.externalid && String(off.externalid).toLowerCase().includes(query)) ||
                                (off.description && String(off.description).toLowerCase().includes(query))
                        ).slice(0, 50);
                });

                const onCreateOfferingInput = () => {
                        createOfferingDropdownOpen.value = true;
                        if (selectedCreateOffering.value) {
                                const label = selectedCreateOffering.value.name || String(selectedCreateOffering.value.id);
                                if (createOfferingSearch.value !== label) {
                                        selectedCreateOffering.value = null;
                                }
                        }
                };

                const selectCreateOffering = (off: OfferingModel) => {
                        selectedCreateOffering.value = off;
                        createOfferingSearch.value = off.name || String(off.id);
                        createOfferingDropdownOpen.value = false;
                };

                const clearCreateOffering = () => {
                        selectedCreateOffering.value = null;
                        createOfferingSearch.value = '';
                };

                const closeCreateModal = () => {
                        showCreateModal.value = false;
                        newThemeName.value = '';
                        selectedCreateOffering.value = null;
                        createOfferingSearch.value = '';
                        createOfferingDropdownOpen.value = false;
                };

                const handleCreateOfferingClickOutside = (event: MouseEvent) => {
                        const el = createOfferingDropdownRef.value;
                        if (!el) return;
                        if (!el.contains(event.target as Node)) {
                                createOfferingDropdownOpen.value = false;
                                if (selectedCreateOffering.value) {
                                        createOfferingSearch.value = selectedCreateOffering.value.name || String(selectedCreateOffering.value.id);
                                }
                        }
                };

                const createItem = async () => {
                        if (!newThemeName.value.trim() || !selectedCreateOffering.value) return;

                        isCreating.value = true;
                        try {
                                const offering = selectedCreateOffering.value;
                                const payload: any = {
                                        name: newThemeName.value.trim(),
                                        groupid: offering.groupid,
                                        typeid: offering.typeid,
                                };

                                const newTheme = await ThemeService.create(payload);
                                closeCreateModal();
                                router.push(`/theme/${newTheme.id}`);
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create theme',
                                        icon: 'error',
                                });
                        } finally {
                                isCreating.value = false;
                        }
                };

                const filteredFilterOfferings = computed(() => {
                        const query = filterOfferingSearch.value.toLowerCase().trim();
                        const source = offerings.value;
                        if (!query) return source.slice(0, 50);
                        return source.filter(off =>
                                String(off.id).includes(query) ||
                                String(off.name ?? '').toLowerCase().includes(query) ||
                                (off.externalid && String(off.externalid).toLowerCase().includes(query)) ||
                                (off.description && String(off.description).toLowerCase().includes(query))
                        ).slice(0, 50);
                });

                const onFilterOfferingInput = () => {
                        filterOfferingDropdownOpen.value = true;
                };

                const selectFilterOffering = (off: OfferingModel) => {
                        selectedFilterOffering.value = off;
                        selectedOfferingId.value = String(off.id);
                        filterOfferingSearch.value = off.name || String(off.id);
                        filterOfferingDropdownOpen.value = false;
                        loadData();
                };

                const clearFilterOffering = () => {
                        const wasSelected = !!selectedFilterOffering.value;
                        selectedFilterOffering.value = null;
                        selectedOfferingId.value = '';
                        filterOfferingSearch.value = '';
                        filterOfferingDropdownOpen.value = false;
                        if (wasSelected) loadData();
                };

                const handleFilterOfferingClickOutside = (event: MouseEvent) => {
                        const el = filterOfferingDropdownRef.value;
                        if (!el) return;
                        if (!el.contains(event.target as Node)) {
                                filterOfferingDropdownOpen.value = false;
                                if (selectedFilterOffering.value) {
                                        filterOfferingSearch.value = selectedFilterOffering.value.name || String(selectedFilterOffering.value.id);
                                }
                        }
                };

                const filteredImportOfferings = computed(() => {
                        const query = importOfferingSearch.value.toLowerCase().trim();
                        const source = offerings.value;
                        if (!query) return source.slice(0, 50);
                        return source.filter(off =>
                                String(off.id).includes(query) ||
                                String(off.name ?? '').toLowerCase().includes(query) ||
                                (off.externalid && String(off.externalid).toLowerCase().includes(query)) ||
                                (off.description && String(off.description).toLowerCase().includes(query))
                        ).slice(0, 50);
                });

                const selectImportOffering = (off: OfferingModel) => {
                        selectedImportOffering.value = off;
                        importOfferingSearch.value = off.name || String(off.id);
                        importOfferingDropdownOpen.value = false;
                };

                const clearImportOffering = () => {
                        selectedImportOffering.value = null;
                        importOfferingSearch.value = '';
                };

                const triggerImportFileInput = () => {
                        importFileInputRef.value?.click();
                };

                const getThemeFileKind = (file: File): 'json' | 'zip' | null => {
                        const name = file.name.toLowerCase();
                        if (name.endsWith('.json')) return 'json';
                        if (name.endsWith('.zip')) return 'zip';
                        const type = (file.type || '').toLowerCase();
                        if (type === 'application/json') return 'json';
                        if (type === 'application/zip' || type === 'application/x-zip-compressed') return 'zip';
                        return null;
                };

                const showInvalidImportFileError = () => {
                        Swal.fire({
                                title: 'Invalid file type',
                                text: 'Please select a JSON or ZIP file.',
                                icon: 'error',
                        });
                };

                const handleImportFileSelect = (event: Event) => {
                        const input = event.target as HTMLInputElement;
                        const file = input.files?.[0];
                        if (!file) return;
                        if (!getThemeFileKind(file)) {
                                input.value = '';
                                showInvalidImportFileError();
                                return;
                        }
                        importFile.value = file;
                };

                const handleImportFileDrop = (event: DragEvent) => {
                        importIsDragging.value = false;
                        const file = event.dataTransfer?.files?.[0];
                        if (!file) return;
                        if (!getThemeFileKind(file)) {
                                showInvalidImportFileError();
                                return;
                        }
                        importFile.value = file;
                };

                const formatFileSize = (bytes: number): string => {
                        if (bytes < 1024) return bytes + ' B';
                        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
                };

                const closeImportModal = () => {
                        showImportModal.value = false;
                        importOfferingSearch.value = '';
                        importOfferingDropdownOpen.value = false;
                        selectedImportOffering.value = null;
                        importFile.value = null;
                        importIsDragging.value = false;
                };

                const importTheme = async () => {
                        if (!selectedImportOffering.value || !importFile.value) return;

                        isImporting.value = true;
                        try {
                                const formData = new FormData();
                                formData.append('file', importFile.value);

                                const uploadResponse = await httpClient.post('/api/upload', formData, {
                                        headers: {
                                                'Content-Type': 'multipart/form-data',
                                        },
                                });

                                let fileUrl: string;
                                if (uploadResponse.data?.url) {
                                        fileUrl = uploadResponse.data.url;
                                } else if (typeof uploadResponse.data === 'string') {
                                        fileUrl = uploadResponse.data;
                                } else {
                                        throw new Error('Upload did not return a valid URL');
                                }

                                const fileKind = getThemeFileKind(importFile.value);
                                if (!fileKind) {
                                        throw new Error('Please select a JSON or ZIP file.');
                                }
                                const endpoint = fileKind === 'zip' ? '/api/theme/package' : '/api/theme/import';
                                await httpClient.post(endpoint, {
                                        url: fileUrl,
                                        groupid: selectedImportOffering.value.groupid,
                                        typeid: selectedImportOffering.value.typeid,
                                });

                                closeImportModal();
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Theme imported successfully',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                                await loadData();
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to import theme',
                                        icon: 'error',
                                });
                        } finally {
                                isImporting.value = false;
                        }
                };

                onMounted(async () => {
                        document.addEventListener('mousedown', handleFilterOfferingClickOutside);
                        document.addEventListener('mousedown', handleCreateOfferingClickOutside);
                        await loadOfferings();
                        await loadData();
                });

                onBeforeUnmount(() => {
                        document.removeEventListener('mousedown', handleFilterOfferingClickOutside);
                        document.removeEventListener('mousedown', handleCreateOfferingClickOutside);
                });

                return {
                        items, offerings, selectedOfferingId, searchQuery, currentPage, pageSize, isLoading,
                        selectedFilterOffering, filterOfferingSearch, filterOfferingDropdownOpen, filterOfferingDropdownRef,
                        filteredFilterOfferings, onFilterOfferingInput, selectFilterOffering, clearFilterOffering,
                        showCreateModal, newThemeName, isCreating,
                        selectedCreateOffering, createOfferingSearch, createOfferingDropdownOpen, createOfferingDropdownRef,
                        filteredCreateOfferings, onCreateOfferingInput, selectCreateOffering, clearCreateOffering, closeCreateModal,
                        filteredItems, totalRows, totalPages, startRow, endRow, paginatedItems, visiblePages,
                        goToPage, loadData, editItem, confirmDelete, createItem,
                        showImportModal, importOfferingSearch, importOfferingDropdownOpen, selectedImportOffering,
                        importFile, importIsDragging, isImporting, filteredImportOfferings, importFileInputRef,
                        selectImportOffering, clearImportOffering, triggerImportFileInput,
                        handleImportFileSelect, handleImportFileDrop,
                        formatFileSize, closeImportModal, importTheme,
                };
        },
});
</script>
